/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import Model, { Providers } from "@tripian/model";
import classes from "./GygTourInfoForm.module.scss";
import {
  NumberCounter,
  DatePicker,
  Button,
  CustomPopover,
  SvgIcons,
} from "@tripian/react";
import * as gtag from "../../../../gtag";

interface IGygTourInfoForm {
  formPersonsCategories: Providers.Gyg.PersonCategory[];
  personsCategories: (Providers.Gyg.PersonCategory & { count: number })[];
  setPersonsCategories: (
    personsCategories: (Providers.Gyg.PersonCategory & { count: number })[]
  ) => void;
  availableDates: string[];
  availableDate?: string;
  setAvailableDate: (availableDate: string) => void;
  checkAvailabilityClick: (
    tourOptionsRequest: Providers.Gyg.TourPriceBreakdownRequest
  ) => void;
  onScrollToAvailability?: () => void;
  t: (value: Model.TranslationKey) => string;
}

const GygTourInfoForm: React.FC<IGygTourInfoForm> = ({
  formPersonsCategories,
  personsCategories,
  setPersonsCategories,
  availableDates,
  availableDate,
  setAvailableDate,
  checkAvailabilityClick,
  onScrollToAvailability,
  t,
}) => {
  moment.locale(window.twindow.langCode);

  const [clicked, setClicked] = useState<boolean>(false);
  const [showPopOver, setShowPopOver] = useState<boolean>(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState<boolean>(false);

  const checkAvailabilityOnClick = () => {
    const ticketCounts = {};
    personsCategories.forEach(ticket => {
      ticketCounts[ticket.ticket_category] = ticket.count;
    });

    const tourOptionsRequest: Providers.Gyg.TourPriceBreakdownRequest = {
      base_data: {
        currency: "USD",
      },
      data: {
        date: availableDate || "",
        participants: ticketCounts,
      },
    };
    checkAvailabilityClick(tourOptionsRequest);
  };

  const backdropClickHandler = (e: MouseEvent) => {
    e.stopPropagation();
    setShowPopOver(false);
  };

  const isButtonDisabled =
    availableDates.length === 0 ||
    !availableDate ||
    personsCategories.every(category => category.count === 0);

  const hasAutoTriggered = useRef(false);
  useEffect(() => {
    if (!isButtonDisabled && !hasAutoTriggered.current) {
      hasAutoTriggered.current = true;
      checkAvailabilityOnClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isButtonDisabled]);

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 w-full">
      {availableDates.length === 0 ? (
        <div className="row mb0 bg-white py-4 rounded-2xl flex items-center justify-center shadow-lg text-primary-color">
          <span className={classes.warningMessage}>
            {t("trips.myTrips.localExperiences.tourDetails.noAvailableDays")}
          </span>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-4 mb-4">
            <div className={classes.gygTourInfoFormDatePickerWrapper}>
              {availableDate && availableDates.length > 0 && (
                <DatePicker
                  currentDate={moment(availableDate)}
                  startDate={moment(availableDates[0])}
                  endDate={moment(
                    availableDates[availableDates.length - 1]
                  ).add("day", 1)}
                  onchanged={date => {
                    if (date) setAvailableDate(date.format("YYYY-MM-DD"));

                    if (clicked) {
                      checkAvailabilityOnClick();
                    }
                  }}
                />
              )}
            </div>
            <div>
              <CustomPopover
                positions={["bottom"]}
                show={showPopOver}
                backdropClick={backdropClickHandler}
                content={
                  <div className={classes.gygTourInfoListItemWrapper}>
                    {formPersonsCategories.map((formPersonsCategory, i) => {
                      const count: number =
                        personsCategories.find(
                          x =>
                            x.ticket_category ===
                            formPersonsCategory.ticket_category
                        )?.count ?? 0;
                      return (
                        <li
                          key={`${formPersonsCategory.name}-${i}`}
                          className={classes.gygTourInfoFormListItem}
                        >
                          <div>
                            <h4 className={classes.gygTourInfoPersonCountText}>
                              <div>{formPersonsCategory.name}</div>
                            </h4>
                            <div className={classes.gygTourInfoPersonAge}>
                              (
                              {t(
                                "trips.myTrips.localExperiences.tourDetails.age"
                              )}
                              : {formPersonsCategory.age_range.min} -{" "}
                              {formPersonsCategory.age_range.max})
                            </div>
                          </div>

                          <div className={classes.gygTourInfoPersonCount}>
                            {personsCategories.length > 0 && (
                              <NumberCounter
                                defaultValue={count}
                                minValue={0}
                                maxValue={100}
                                iconFill="blue"
                                onchange={newCount => {
                                  const newPersonsCategories = [
                                    ...personsCategories,
                                  ];
                                  const findX = newPersonsCategories.findIndex(
                                    x =>
                                      x.ticket_category ===
                                      formPersonsCategory.ticket_category
                                  );
                                  if (findX > -1) {
                                    newPersonsCategories[findX].count =
                                      newCount;
                                    setPersonsCategories(newPersonsCategories);
                                  }

                                  if (clicked) {
                                    checkAvailabilityOnClick();
                                  }
                                }}
                              />
                            )}
                          </div>
                        </li>
                      );
                    })}
                    <Button
                      disabled={isButtonDisabled}
                      className="w-full mt-4"
                      onClick={() => {
                        checkAvailabilityOnClick();
                        setShowPopOver(false);
                      }}
                      text={t(
                        "trips.myTrips.itinerary.step.addToItinerary.submit.apply"
                      )}
                    />
                  </div>
                }
              >
                <div
                  onClick={() => {
                    setShowPopOver(!showPopOver);
                  }}
                  role="button"
                  onKeyDown={() => {}}
                  tabIndex={0}
                  className={classes.gygTourInfoPeopleButton}
                >
                  <SvgIcons.Avatar />
                  {personsCategories
                    .filter(category => category.count > 0)
                    .map((category, index, self) => {
                      const comma = index + 1 !== self.length ? "," : "";
                      return (
                        <span
                          key={`${category.ticket_category}-${category.count}-${index}`}
                        >
                          {category.count} {category.name}
                          {comma}
                        </span>
                      );
                    })}
                </div>
              </CustomPopover>
            </div>
          </div>
          <Button
            disabled={isButtonDisabled || isCheckingAvailability}
            className="w-full mt-4 text-lg py-3"
            onClick={() => {
              gtag.googleAnalyticsEvent("tour_book_click", { provider: "gyg" });
              onScrollToAvailability?.();
              setIsCheckingAvailability(true);
              checkAvailabilityOnClick();
              setClicked(true);
              setTimeout(() => setIsCheckingAvailability(false), 2000);
            }}
            color="primary"
            text={isCheckingAvailability ? "..." : t(
              "trips.myTrips.localExperiences.tourDetails.checkAvailability"
            )}
          />
        </>
      )}
    </div>
  );
};

export default GygTourInfoForm;
